import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { APPLY_URL } from '../mock';

export const ImmersiveHero = ({ onApplyClick }) => {
  const heroRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { scrollYProgress } = useScroll();
  
  const y = useTransform(scrollYProgress, [0, 1], [0, 500]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.3], [1, 1.2]);
  
  const smoothMouseX = useSpring(0, { stiffness: 100, damping: 30 });
  const smoothMouseY = useSpring(0, { stiffness: 100, damping: 30 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 20;
      setMousePosition({ x, y });
      smoothMouseX.set(x);
      smoothMouseY.set(y);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [smoothMouseX, smoothMouseY]);

  return (
    <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* 3D Background with Parallax */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ 
          scale,
          rotateX: useTransform(smoothMouseY, [-20, 20], [2, -2]),
          rotateY: useTransform(smoothMouseX, [-20, 20], [-2, 2])
        }}
      >
        <img
          src="https://customer-assets.emergentagent.com/job_driven-12week/artifacts/8izh3745_IMG_6842.jpeg"
          alt="Clifton Tant at desk - CT3 Performance Coaching"
          className="w-full h-full object-cover object-center"
          style={{
            transform: `translate3d(${mousePosition.x}px, ${mousePosition.y}px, 0)`,
            transition: 'transform 0.3s ease-out'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/65 to-[#070707]/90"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#070707]/65 via-[#070707]/25 to-[#070707]/65"></div>
      </motion.div>

      {/* Floating CT3 Logo - shield shape with elegant motion */}
      <motion.div
        className="absolute top-2 left-2 md:top-6 md:left-6 w-16 h-16 md:w-32 md:h-32 z-20 pointer-events-none"
        style={{
          x: useTransform(smoothMouseX, [-20, 20], [-8, 8]),
          y: useTransform(smoothMouseY, [-20, 20], [-8, 8]),
          perspective: '800px',
          transformStyle: 'preserve-3d'
        }}
        animate={{
          y: [0, -8, 0],
          rotateY: [0, 15, 0, -15, 0],
        }}
        transition={{
          y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
          rotateY: { duration: 8, repeat: Infinity, ease: "easeInOut" }
        }}
      >
        {/* Soft radial backdrop so logo's black bg blends seamlessly */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(7,7,7,0.95) 30%, rgba(7,7,7,0.7) 55%, transparent 75%)'
          }}
        />
        <motion.img
          src="https://customer-assets.emergentagent.com/job_driven-12week/artifacts/6khqy6w1_IMG_6901.png"
          alt="CT3 Performance Logo"
          className="relative w-full h-full object-contain"
          style={{ mixBlendMode: 'screen' }}
          animate={{
            opacity: [0.9, 1, 0.9],
            filter: [
              'drop-shadow(0 0 8px rgba(198, 160, 74, 0.5))',
              'drop-shadow(0 0 20px rgba(198, 160, 74, 0.85))',
              'drop-shadow(0 0 8px rgba(198, 160, 74, 0.5))'
            ]
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>

      <motion.div
        className="absolute bottom-20 right-10 w-24 h-24 border-2 border-[#d9be7e]/20 backdrop-blur-sm"
        style={{
          rotateX: useTransform(smoothMouseY, [-20, 20], [15, -15]),
          rotateY: useTransform(smoothMouseX, [-20, 20], [-15, 15]),
          x: useTransform(smoothMouseX, [-20, 20], [30, -30]),
          y: useTransform(smoothMouseY, [-20, 20], [30, -30])
        }}
        animate={{
          rotate: [360, 270, 180, 90, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      {/* Content with 3D Transform */}
      <motion.div 
        className="relative z-10 max-w-6xl mx-auto px-6 pt-24 pb-20 md:py-20 text-center"
        style={{ 
          opacity,
          y
        }}
      >
        <motion.div 
          className="space-y-8 w-full max-w-full"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          {/* Overline with 3D effect */}
          <motion.p 
            className="font-accent text-gold text-xs sm:text-sm md:text-base lg:text-lg italic tracking-[0.15em] md:tracking-[0.2em] uppercase px-2"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            The CT3 12 · 24 · 52-Week System · 1:1 Coaching
          </motion.p>

          {/* Main Headline — Brand promise as H1 */}
          <div className="relative inline-block px-3 md:px-8 py-4 md:py-8 max-w-full">
            {/* Dark backdrop for readability */}
            <div className="absolute inset-0 bg-[#070707]/60 backdrop-blur-sm border border-gold/20" />
            <div className="relative z-10 space-y-1">
              <motion.h1 
                className="font-hero text-[1.65rem] sm:text-4xl md:text-6xl lg:text-7xl uppercase tracking-wide leading-[1.05] text-cream"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                style={{ fontWeight: 700, textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
              >
                You've Mastered
              </motion.h1>
              <motion.h1 
                className="font-hero text-[1.65rem] sm:text-4xl md:text-6xl lg:text-7xl uppercase tracking-wide leading-[1.05] text-cream"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.55, duration: 0.8 }}
                style={{ fontWeight: 700, textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
              >
                Everything Except
              </motion.h1>
              <motion.h1 
                className="font-hero text-[1.65rem] sm:text-4xl md:text-6xl lg:text-7xl uppercase tracking-wide leading-[1.05] text-gold"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                style={{ fontWeight: 700, textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
              >
                Your Body
              </motion.h1>
            </div>
          </div>

          {/* Subhead — italic serif, smaller (was the H1 before) */}
          <motion.p 
            className="font-accent text-base sm:text-lg md:text-2xl lg:text-3xl italic text-cream/90 max-w-3xl mx-auto leading-tight pt-2 px-4"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            style={{ textShadow: '0 2px 15px rgba(0,0,0,0.9)' }}
          >
            Get visibly leaner and measurably stronger in <span className="text-gold">12, 24, or 52 weeks</span>.
          </motion.p>

          {/* Promise line */}
          <motion.p 
            className="font-body text-xs sm:text-sm md:text-lg text-[#e8e6df] max-w-3xl mx-auto leading-relaxed font-light pt-1 px-4"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.9)' }}
          >
            For driven professionals who win at work but keep losing to their own fitness — without the time to live in a gym.
          </motion.p>

          {/* CTA with 3D Hover */}
          <motion.div 
            className="pt-8"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.2, duration: 0.6, type: "spring" }}
          >
            <motion.div
              whileHover={{ 
                scale: 1.05,
                rotateX: 5,
                rotateY: 5,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.95 }}
              style={{ perspective: '1000px' }}
            >
              <Button
                onClick={() => window.open(APPLY_URL, '_blank', 'noopener,noreferrer')}
                size="lg"
                className="bg-[#c6a04a] hover:bg-[#d9be7e] text-[#0d0d0d] font-semibold text-sm md:text-lg px-6 md:px-12 py-5 md:py-7 rounded-none uppercase tracking-wider transition-all duration-300 shadow-2xl shadow-[#c6a04a]/50 border-2 border-[#c6a04a] whitespace-normal h-auto"
              >
                Apply for Your<br className="md:hidden" /> Strategy Call
                <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
              </Button>
            </motion.div>
            <motion.p 
              className="font-body text-xs text-gold-soft mt-4 uppercase tracking-widest"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              Takes 60 seconds · By application only
            </motion.p>
          </motion.div>

          {/* Stats Row with Stagger */}
          <motion.div 
            className="pt-12 grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 max-w-4xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.1,
                  delayChildren: 1.6
                }
              }
            }}
          >
            {['12 / 24 / 52 Weeks', 'Custom Training', 'Precision Nutrition', 'Weekly Check-Ins', 'Proven Results'].map((stat, i) => (
              <motion.div
                key={i}
                className="bg-[#0d0d0d]/80 backdrop-blur-sm border border-[#c6a04a]/30 p-4 hover:border-[#c6a04a] transition-all duration-300"
                variants={{
                  hidden: { opacity: 0, y: 50, rotateX: -90 },
                  visible: { opacity: 1, y: 0, rotateX: 0 }
                }}
                whileHover={{ 
                  scale: 1.05,
                  rotateY: 5,
                  transition: { duration: 0.2 }
                }}
              >
                <p className="font-body text-xs md:text-sm text-gold uppercase tracking-wider">{stat}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator with Animation */}
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
        animate={{
          y: [0, 10, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <div className="w-6 h-10 border-2 border-gold rounded-full flex justify-center pt-2">
          <motion.div 
            className="w-1 h-2 bg-gold rounded-full"
            animate={{
              y: [0, 20, 0],
              opacity: [1, 0, 1]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>
      </motion.div>
    </section>
  );
};
